import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { historyService } from '../services/historyService';
import { authService } from '../services/authService';
import keycloak, { keycloakEnabled } from '../keycloak';
import { isClientHost } from '../lib/host';
import { shareSignupHref } from '../lib/referralRef';
import { claimShareView } from '../lib/shareViews';
import { exportSharedPdf } from './protocol/ExportUtils';

// Порядок вкладок и выбор первой: протокол вперёд транскрипта. Тот, кому прислали
// ссылку, почти всегда хочет прочитать итог встречи, а не сырую расшифровку на
// сотни строк — к ней он перейдёт сам, если понадобится.
const TAB_ORDER = ['protocol', 'transcript'];

const orderedTabs = (includes = []) => [
  ...TAB_ORDER.filter((p) => includes.includes(p)),
  ...includes.filter((p) => !TAB_ORDER.includes(p)),
];

// Протокол выбираем, только если он действительно есть текстом: 'protocol' попадает
// в includes по желанию владельца, а summary при этом может быть пустым — открыть
// такую вкладку значило бы встретить человека пустым экраном.
const defaultTab = (data) => {
  const tabs = orderedTabs(data?.includes);
  if (tabs.includes('protocol') && !data?.summary?.text) {
    return tabs.find((p) => p !== 'protocol') || 'protocol';
  }
  return tabs[0] || null;
};

/**
 * Публичная read-only страница по ссылке /s/:token. Без авторизации.
 * Рендерится из index.js до гейта логина.
 */
export default function PublicSharePage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState(null);
  // «Сохранить в Историю»: null → saving → { id } | { error }.
  const [saved, setSaved] = useState(null);
  // «Скачать PDF»: null → 'busy' → null | { error }.
  const [pdf, setPdf] = useState(null);
  // «Копировать»: null → 'ok' | 'fail', через две секунды обратно.
  const [copied, setCopied] = useState(null);
  const copiedTimer = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const save = async () => {
    setSaved('saving');
    try {
      // Страница ссылки продление токена не ставит (его ставит App.js), а читают её
      // дольше пяти минут жизни JWT — продлеваем перед запросом.
      await authService.refreshSession();
      const { id } = await historyService.saveShared(token);
      setSaved({ id });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setSaved({ error: typeof detail === 'string' ? detail : 'Не удалось сохранить — попробуйте позже' });
    }
  };

  // Не вошедшего — на вход и обратно с меткой save=1, тогда сохранение пройдёт само.
  // Клиентский домен Keycloak не поднимает (index.js), там вход по коду на витрине:
  // туда же ведёт «Попробовать бесплатно», после входа человек нажмёт ещё раз.
  const saveOrLogin = () => {
    if (Object.keys(authService.getAuthHeaders()).length) return save();
    if (keycloakEnabled && keycloak && !isClientHost()) {
      keycloak.login({ redirectUri: `${window.location.origin}${window.location.pathname}?save=1` });
      return undefined;
    }
    window.location.href = shareSignupHref(token);
    return undefined;
  };

  useEffect(() => {
    // Открытие страницы видно автору («Открыли N раз») — раз на вкладку.
    historyService.getShared(token, { view: claimShareView(token) })
      .then((d) => { setData(d); setTab(defaultTab(d)); })
      .catch(() => setError(true));
  }, [token]);

  // Вернулся со входа с меткой — сохраняем один раз и убираем метку из адреса,
  // чтобы обновление страницы не сохраняло снова.
  useEffect(() => {
    if (!data || new URLSearchParams(window.location.search).get('save') !== '1') return;
    window.history.replaceState({}, '', window.location.pathname);
    if (Object.keys(authService.getAuthHeaders()).length) save();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Ссылки относительные: страницу открывают и с ys.1bitai.ru, и с newton.1bitai.ru,
  // и получатель должен попасть на витрину ТОГО домена, по которому пришёл, — иначе
  // сотрудника уводит на клиентский вход, а клиента на внутренний.
  const Brand = () => (
    <div className="flex items-center gap-2 bg-paper-card border-b border-paper-line px-5 py-3">
      <a href="/" className="font-extrabold text-paper-accent text-[15px] no-underline hover:opacity-80 transition-opacity">
        <span className="text-paper-ink">Бит.</span>Ньютон
      </a>
      <span className="flex-1" />
      {data && <span className="text-[11px] text-paper-ink-3 bg-paper-bg rounded-full px-2.5 py-1">Только просмотр</span>}
    </div>
  );

  if (error) {
    return (
      <div className="min-h-screen bg-paper-bg">
        <Brand />
        <div className="max-w-lg mx-auto mt-16 bg-paper-card rounded-card p-9 text-center">
          <div className="text-3xl">🔗</div>
          <h3 className="mt-3 mb-1.5 text-paper-ink font-semibold">Ссылка недоступна</h3>
          <p className="text-paper-ink-3 text-sm">Срок действия ссылки истёк или владелец отозвал доступ.</p>
          {/* Протухшая ссылка — всё равно вход в продукт: расшифровку человек не
              получит, но может сделать свою. Привязка к поделившемуся сохраняется —
              bit-history отдаёт владельца по хешу независимо от срока ссылки
              (решение владельца 17.09.2026). */}
          <p className="mt-5 text-[13px] leading-relaxed text-paper-ink-2">
            Но попробовать можно и без неё — бесплатно: загрузите свою запись, Бит.Ньютон
            расшифрует её и соберёт протокол. Результат будет даже лучше: это будет ваша
            встреча, а не чужая.
          </p>
          <a href={shareSignupHref(token)}
             className="mt-4 inline-block rounded-control bg-paper-accent px-5 py-[10px] text-[14px] font-semibold
                        text-white no-underline hover:bg-paper-accent-hover transition-colors">
            Попробовать бесплатно →
          </a>
        </div>
      </div>
    );
  }

  if (!data) {
    return (<div className="min-h-screen bg-paper-bg"><Brand /><div className="text-center text-paper-hint mt-20">Загрузка…</div></div>);
  }

  const content = tab === 'protocol' ? (data.summary?.text || '') : (data.transcript || '');
  // Скачивание — PDF с сохранённой структурой, копирование — Markdown (обращение d8f798a9, #67).
  // Раньше обе кнопки отдавали один и тот же Markdown, «Скачать» — в .txt.
  const download = async () => {
    setPdf('busy');
    try {
      await exportSharedPdf(token, tab,
        `${data.title || 'newton'} — ${tab === 'protocol' ? 'протокол' : 'расшифровка'}.pdf`);
      setPdf(null);
    } catch (e) {
      setPdf({ error: e.message });
    }
  };
  const copy = async () => {
    let result = 'ok';
    try {
      // Без TLS и в старых WebView буфера нет вовсе — это не «скопировано».
      if (!navigator.clipboard) throw new Error('no clipboard');
      await navigator.clipboard.writeText(content);
    } catch (e) {
      result = 'fail';
    }
    setCopied(result);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(null), 2000);
  };
  const label = (p) => (p === 'transcript' ? 'Транскрибация' : 'Протокол');

  return (
    // Верстка по артборду «Публичная ссылка» (docs/redesign/2026-09-04-bumaga/):
    // колонка по центру шириной 860px, карточка со скруглением 20 и щедрыми
    // полями. Эту страницу видят снаружи компании и без входа — она единственная,
    // где неряшливость заметна не нам.
    <div className="min-h-screen bg-paper-bg flex flex-col items-center gap-4 px-5 pb-16 pt-7">
      <div className="flex w-full max-w-[860px] items-center justify-between gap-4">
        <Brand />
        {data.share?.expires_at && (
          <span className="text-[12px] text-paper-ink-3">
            Ссылка действует до {new Date(data.share.expires_at).toLocaleDateString('ru-RU',
              { day: 'numeric', month: 'long' })} · открыто без входа
          </span>
        )}
      </div>

      <div className="flex w-full max-w-[860px] flex-col gap-[22px] rounded-[20px] bg-paper-card
                      px-11 pb-7 pt-9">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-paper-ink-3">
            {tab === 'protocol' ? 'Протокол встречи' : 'Расшифровка встречи'}
          </div>
          <h1 className="m-0 mt-[10px] font-display text-[34px] font-extrabold leading-[1.05]
                         tracking-[-0.035em] text-paper-ink">
            {data.title}
          </h1>
          {data.meta?.duration && (
            <div className="mt-[10px] text-[13px] text-paper-ink-3">
              Запись · {Math.round(data.meta.duration / 60)} мин
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1">
          {orderedTabs(data.includes).map((p) => (
            <button key={p} type="button" onClick={() => { setTab(p); setPdf(null); }}
              className={`rounded-chip px-3 py-[6px] text-[13px] font-medium transition-colors ${
                tab === p ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'}`}>
              {label(p)}
            </button>
          ))}
          <span className="flex-1" />
          <button type="button" onClick={copy}
                  className="rounded-chip bg-paper-bg px-3 py-[6px] text-[13px] font-medium text-paper-ink
                             hover:bg-paper-panel transition-colors">
            {copied === 'ok' ? 'Скопировано' : copied === 'fail' ? 'Буфер обмена недоступен' : 'Копировать как Markdown'}
          </button>
          <button type="button" onClick={download} disabled={pdf === 'busy'}
                  className="rounded-chip bg-paper-bg px-3 py-[6px] text-[13px] font-medium text-paper-ink
                             hover:bg-paper-panel transition-colors disabled:opacity-60">
            {pdf === 'busy' ? 'Готовим PDF…' : 'Скачать PDF'}
          </button>
          {saved?.id ? (
            <a href={`/?history=${encodeURIComponent(saved.id)}`}
               className="rounded-chip bg-paper-ink px-3 py-[6px] text-[13px] font-medium text-white
                          no-underline hover:opacity-90 transition-opacity">
              Сохранено · Открыть в Истории →
            </a>
          ) : (
            <button type="button" onClick={saveOrLogin} disabled={saved === 'saving'}
                    className="rounded-chip bg-paper-accent px-3 py-[6px] text-[13px] font-semibold text-white
                               hover:bg-paper-accent-hover transition-colors disabled:opacity-60">
              {saved === 'saving' ? 'Сохраняем…' : 'Сохранить в Историю'}
            </button>
          )}
        </div>
        {saved?.error && (
          <div className="-mt-3 text-right text-[13px] text-destructive">{saved.error}</div>
        )}
        {pdf?.error && (
          <div className="-mt-3 text-right text-[13px] text-destructive">{pdf.error}</div>
        )}

        {tab === 'protocol' ? (
          <div className="prose prose-sm max-w-[70ch] text-paper-ink prose-headings:font-bold
                          prose-headings:text-paper-ink prose-h1:text-[19px] prose-h2:text-[17px]
                          prose-h3:text-[15px] prose-strong:text-paper-ink prose-p:leading-[1.55]
                          prose-p:text-[15px] prose-li:text-[15px]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        ) : (
          <pre className="max-w-[70ch] whitespace-pre-wrap font-montserrat text-[15px] leading-[1.55] text-paper-ink">
            {content}
          </pre>
        )}
      </div>

      <div className="flex w-full max-w-[860px] flex-col items-center gap-3 text-center">
        <div className="text-[13px] text-paper-ink-2">
          Расшифровка и протокол собраны в Бит.Ньютон.
        </div>
        {/* На клиентский вход с токеном ссылки: bit-auth привяжет новую учётку к тому,
            кто поделился (docs/superpowers/specs/2026-09-16-share-referral-attribution-design.md).
            Домен другой, localStorage не общий — ref едет в URL. Без CLIENT_HOST (on-prem)
            остаёмся на своём домене. */}
        <a href={shareSignupHref(token)}
           className="rounded-control bg-paper-accent px-5 py-[10px] text-[14px] font-semibold
                      text-white no-underline hover:bg-paper-accent-hover transition-colors">
          Попробовать бесплатно →
        </a>
      </div>
    </div>
  );
}
